import {
	ComparisonOperatorDTOKeywordEnum,
	ConceptDefinitionDTODataTypeEnum,
	ConfidenceLevelEnum,
	ItemTypeEnum,
	ParameterizedConceptDTORelativenessModeEnum,
	ParameterizedConceptDTORepresentationModeEnum,
	SourceEnum,
	TimeHorizonEnum,
	type ConceptDefinitionDTO,
} from "@eu.mdotm.api/smart-workspaces";
import {
	workspaceChainModel,
	workspaceChainPredicateValueModel,
	type WorkspaceChain,
	type WorkspaceChainPredicate,
} from "./type";
import z from "zod";
import { fakeTagConcept } from "./ChainableElement";

/**
 * The concept definition, restated as validation — the one restatement in this file that is *not* derived,
 * and the `satisfies` is why it can be one.
 *
 * A generated DTO cannot be inferred from zod: `ConceptDefinitionDTO` declares every field optional, while a
 * concept the chain can actually work with has an `id` and a `dataType`. So the two say different things on
 * purpose, and `keyof` is what keeps them saying it about the same *fields*.
 */
const conceptDefinitionSchema = z
	.object({
		id: z.string(),
		description: z.string().optional(),
		defaultLabel: z.string().optional(),
		supportedTypes: z.array(z.nativeEnum(ItemTypeEnum)).optional(),
		dataType: z.nativeEnum(ConceptDefinitionDTODataTypeEnum),
		numericDecimalPlaces: z.number().optional(),
		unitOfMeasurement: z.string().optional(),
		allowedComparisonOperators: z.array(z.nativeEnum(ComparisonOperatorDTOKeywordEnum)).optional(),
		allowedTimeHorizons: z.array(z.nativeEnum(TimeHorizonEnum)).optional(),
		allowedConfidenceLevels: z.array(z.nativeEnum(ConfidenceLevelEnum)).optional(),
		representationModes: z.array(z.nativeEnum(ParameterizedConceptDTORepresentationModeEnum)).optional(),
		relativenessModes: z.array(z.nativeEnum(ParameterizedConceptDTORelativenessModeEnum)).optional(),
		sortable: z.boolean().optional(),
		taxonomy: z.array(z.string()).optional(),

		allowedSource: z.array(z.nativeEnum(SourceEnum)).optional(),
		exposureOptions: z.array(z.string()).optional(),
		editable: z.boolean().optional(),
		shareable: z.boolean().optional(),
		shared: z.boolean().optional(),
		multiTag: z.boolean().optional(),
		managed: z.boolean().optional(),
		breakdownDimensions: z.array(z.string()).optional(),
		// The parameters the concept's history form accepts, when it has one. Validated as the same four
		// enum lists as above rather than as a passthrough object, because the chain reads them in place
		// of the concept's own for a line chart — a malformed form would silently offer no horizons.
		timeSeries: z
			.object({
				allowedSource: z.array(z.nativeEnum(SourceEnum)).optional(),
				allowedTimeHorizons: z.array(z.nativeEnum(TimeHorizonEnum)).optional(),
				allowedConfidenceLevels: z.array(z.nativeEnum(ConfidenceLevelEnum)).optional(),
				representationModes: z.array(z.nativeEnum(ParameterizedConceptDTORepresentationModeEnum)).optional(),
				relativenessModes: z.array(z.nativeEnum(ParameterizedConceptDTORelativenessModeEnum)).optional(),
			})
			.optional(),
	} satisfies Record<keyof ConceptDefinitionDTO, unknown>)
	.optional();

/**
 * What a chain has to answer *when it is submitted*, as opposed to what it may hold while it is being filled
 * in — the only difference between {@link workspaceChainModel} and the schema every panel resolves with.
 *
 * Three fields, and each is a rule rather than a repetition:
 *
 * - `name` is required. A chain is nameless until a concept is picked, so the model cannot demand one, but a
 *   concept is stored under its label and an unnamed one would be a row with a blank header.
 * - `concept` and `availableTagsConcepts` are validated *structurally*, against the definition the catalogue
 *   published. The model carries them through as the DTOs they are (see the note on the model): the chain
 *   reads a concept's published lists in order to render its own steps, so a malformed one would silently
 *   offer nothing to choose from.
 *
 * Keyed on `keyof WorkspaceChain` because it is an *override*: a tightening that names a field the model no
 * longer has would otherwise quietly add a field of its own instead of constraining one.
 */
const workspaceChainSubmitShape = {
	name: z.string(),
	concept: conceptDefinitionSchema,
	availableTagsConcepts: z.array(conceptDefinitionSchema).optional(),
} satisfies Partial<Record<keyof WorkspaceChain, unknown>>;

/**
 * Whether the exposure question the concept asks has actually been answered.
 *
 * Only two concepts ask one: a custom tag (pick a tag) and the risk model (pin at least one level). A
 * breakdown *is* an answer — the level is expanded rather than pinned — so a chain carrying one is done.
 */
function conceptExposureAnswered(chain: WorkspaceChain): boolean {
	// A breakdown *is* the exposure answer — the level is expanded rather than pinned — so the
	// "pick a leaf" requirements below do not apply to a chain that carries one.
	if (chain.breakdown) {
		return true;
	}

	// check if tag options is correctly evaluated
	if (chain.concept?.id?.includes(fakeTagConcept.id!)) {
		return chain.parameters?.tagExposureOption != null;
	}

	// check if risk model is correctly evaluated
	if (chain.concept?.id?.includes("systemId: riskModel")) {
		const riskModelParameters = [
			chain.parameters?.currency,
			chain.parameters?.geography,
			chain.parameters?.macroAssetClass,
			chain.parameters?.microAssetClass,
			chain.parameters?.microGeography,
		];
		return riskModelParameters.some((opt) => opt != null);
	}

	return true;
}

/**
 * A form model, plus the rules that only hold once the user presses Apply — i.e. the resolver schema for any
 * panel built on a chain.
 *
 * Every one of them goes through here rather than intersecting a prebuilt chain schema, and that is the
 * point: `extend` can only add to or constrain the model's fields, never drop one, so a panel field the
 * resolver forgets to declare is no longer expressible. It used to be, and the field was then stripped out
 * of the values the submit handler received, with the form model's type still insisting it was there.
 */
function chainSubmitSchema<Shape extends z.ZodRawShape>(model: z.ZodObject<Shape>) {
	return model.extend(workspaceChainSubmitShape).refine(
		// The cast is the price of being generic over the model: every caller's model extends the chain's, but
		// only the caller knows what it adds.
		(values) => conceptExposureAnswered(values as WorkspaceChain),
		{ message: "Select a valid concept" },
	);
}

/** The chain on its own, submitted: what the panels that add nothing to it resolve with. */
const workspaceChainSchema = chainSubmitSchema(workspaceChainModel);

/**
 * What a filter's condition has to answer at submit time: an operator, and no holes left in its values.
 *
 * The model's tuples are nullable because a half-filled condition has to be representable — `btw` starts out
 * as `[null, null]` — and these are the same tuples with the nulls taken away.
 */
const workspaceChainPredicateSubmitShape = {
	comparisonMode: z.nativeEnum(ComparisonOperatorDTOKeywordEnum),
	comparisonValue: z.union([
		z.tuple([workspaceChainPredicateValueModel]),
		z.tuple([workspaceChainPredicateValueModel, workspaceChainPredicateValueModel]),
		z.tuple([z.array(workspaceChainPredicateValueModel)]),
		z.tuple([z.array(workspaceChainPredicateValueModel), z.array(workspaceChainPredicateValueModel)]),
	]),
} satisfies Partial<Record<keyof WorkspaceChainPredicate, unknown>>;

export { chainSubmitSchema, workspaceChainPredicateSubmitShape, workspaceChainSchema };
